import { Button, buttonVariants } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CustomerDto, useSearchCustomersQuery } from '@/hooks/customerHooks';
import { cn } from '@/lib/utils';
import { CommandLoading } from 'cmdk';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Link } from 'react-router-dom';

interface CustomerSelectDialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (isOpen: boolean) => void;
}

export default function CustomerSelectDialog({
  isDialogOpen,
  setIsDialogOpen,
}: CustomerSelectDialogProps) {
  const [queryText, setQueryText] = useState('');

  const { data: customersListed, status } = useSearchCustomersQuery({ query: queryText });

  const [selectedCustomerGuid, setSelectedCustomerGuid] = useState('');

  const [selectorOpen, setSelectorOpen] = useState(false);

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} defaultOpen={true}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select a customer to add the new device</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Label htmlFor="deviceModel">Customer</Label>
          <Popover
            open={selectorOpen}
            onOpenChange={(change) => {
              setSelectorOpen(change);
              setQueryText('');
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={selectorOpen}
                className="w-full justify-between text-white"
              >
                {selectedCustomerGuid && customersListed
                  ? customersListed.pages
                      .flat()
                      .find((customer) => customer.id === selectedCustomerGuid)?.name
                  : 'Select Customer...'}
                <ChevronsUpDown className="text-lime" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command filter={() => 1}>
                <CommandInput
                  placeholder="Search customers..."
                  className="h-9"
                  onValueChange={setQueryText}
                />
                <CommandList>
                  <CommandEmpty>{status == 'success' && 'No customer found.'}</CommandEmpty>
                  <CommandGroup>
                    {customersListed?.pages.flat().map((customer: CustomerDto) => (
                      <CommandItem
                        key={customer.id}
                        value={customer.id}
                        onSelect={(currentValue) => {
                          setSelectedCustomerGuid(
                            currentValue === selectedCustomerGuid ? '' : currentValue
                          );
                          setSelectorOpen(false);
                        }}
                      >
                        {customer.name}
                        <Check
                          className={cn(
                            'ml-auto',
                            selectedCustomerGuid === customer.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {status === 'pending' && (
                    <CommandLoading>
                      <Spinner />
                    </CommandLoading>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </DialogDescription>
        <DialogFooter className="flex flex-row justify-between">
          <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Link
            to={`/devices/customer/${selectedCustomerGuid}/add-device`}
            className={buttonVariants()}
          >
            Continue
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
